/**
 * Users — slim orchestrator (Phase 7 refactor).
 *
 * Owns: load(users/invites/orgs/tenants), filter+sort, impersonate flow,
 * activate/deactivate/delete, resend/revoke invite. Renders use 4 extracted
 * modules under `components/users/`:
 *
 *   - constants.js (ROLE_LABELS, SORT_FIELDS, etc.)
 *   - UserTables.jsx (ActiveUsersTable, PendingInvitesTable, Th)
 *   - InviteUserDialog.jsx (self-contained invite modal)
 *   - EditUserDialog.jsx (self-contained edit modal)
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useConfirm } from "@/lib/confirm-context";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Search, Users as UsersIcon, ShieldCheck, Clock, SlidersHorizontal, Lock } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useEntitlements } from "@/lib/use-entitlements";
import { ROLE_LABELS, SORT_FIELDS } from "@/components/users/constants";
import { ActiveUsersTable, PendingInvitesTable } from "@/components/users/UserTables";
import InviteUserDialog from "@/components/users/InviteUserDialog";
import EditUserDialog from "@/components/users/EditUserDialog";

// iter-121.99 — KPI cards above the filter row (matches the user-supplied
// Users page screenshot). All counts derive from the same /users + /invites
// payload that drives the tables — no extra round-trip.
function UsersStatsDeck({ totalUsers, activeUsers, pendingInvites }) {
    const tiles = [
        { key: "total",   label: "Total users",        value: totalUsers,      sub: "All users in your platform", Icon: UsersIcon,    plate: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300" },
        { key: "active",  label: "Active users",       value: activeUsers,     sub: "Currently active",           Icon: ShieldCheck,  plate: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300" },
        { key: "pending", label: "Pending invitations", value: pendingInvites, sub: "Invites not accepted",       Icon: Clock,        plate: "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300" },
    ];
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5" data-testid="users-stats">
            {tiles.map((t) => {
                const Icon = t.Icon;
                return (
                    <div key={t.key}
                         className="flex items-center gap-4 rounded-xl border border-border bg-card px-5 py-4 shadow-sm transition-shadow hover:shadow-md"
                         data-testid={`users-stat-${t.key}`}>
                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${t.plate}`}>
                            <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs text-muted-foreground">{t.label}</div>
                            <div className="text-2xl font-semibold tabular-nums leading-tight">{t.value.toLocaleString()}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{t.sub}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function Users() {
    const { user: me } = useAuth();
    const confirm = useConfirm();
    const [users, setUsers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [orgs, setOrgs] = useState([]);
    const [tenantsByOrg, setTenantsByOrg] = useState({});
    const [query, setQuery] = useState("");
    const [orgFilter, setOrgFilter] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState("desc");
    const [tab, setTab] = useState("active");
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const load = async () => {
        const r = await api.get("/users");
        setUsers(r.data);
    };
    const loadInvites = async () => {
        try {
            const r = await api.get("/settings/invitations");
            setInvites(r.data);
        } catch { /* non-admins won't have access */ }
    };
    useEffect(() => { load(); loadInvites(); }, []);

    useEffect(() => {
        if (me?.role === "super_admin") {
            api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
        } else if (me?.role === "org_admin" && me?.org_id) {
            loadTenantsForOrg(me.org_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [me]);

    const loadTenantsForOrg = async (orgId) => {
        if (!orgId || tenantsByOrg[orgId]) return;
        try {
            const r = await api.get(`/organizations/${orgId}/tenants`);
            setTenantsByOrg((prev) => ({ ...prev, [orgId]: r.data }));
        } catch {
            setTenantsByOrg((prev) => ({ ...prev, [orgId]: [] }));
        }
    };

    const canManage = me?.role === "super_admin" || me?.role === "org_admin";
    const { limitReached, openUpgrade } = useEntitlements();
    const usersLimitReached = canManage && me?.role !== "super_admin" && limitReached("max_users");
    const canDelete = canManage;
    // iter-121.33 — Impersonation hierarchy: any admin tier can impersonate
    // STRICTLY LOWER ranks within the slice they manage. Mirrors the backend
    // logic in /api/users/{id}/impersonate so the button only appears for
    // legitimate targets.
    const ROLE_RANK = {
        super_admin: 100, org_admin: 80, entity_admin: 60,
        entity_viewer: 40, viewer: 20,
    };
    const canImpersonate = (u) => {
        if (!me || !u || u.id === me.id) return false;
        if (!u.is_active) return false;
        // super_admin → anyone across the entire platform.
        if (me.role === "super_admin") return true;
        // org_admin → any NON-super_admin user in their OWN org (incl. peer
        // org_admins). Never a super_admin.
        if (me.role === "org_admin") {
            if (u.role === "super_admin") return false;
            return u.org_id === me.org_id;
        }
        // entity_admin → strictly-lower ranks inside their own tenant.
        if (me.role === "entity_admin") {
            if (u.role === "super_admin") return false;
            const tRank = ROLE_RANK[u.role] || 0;
            if (tRank >= ROLE_RANK.entity_admin) return false;
            return u.tenant_id === me.tenant_id;
        }
        return false;
    };

    const impersonate = async (u) => {
        const ok = await confirm({
            title: `Impersonate ${u.name || u.email}?`,
            description: `You'll take over as ${u.email} for up to 30 minutes. Every action is logged and attributed back to you. Click "Exit impersonation" in the banner to return to your own session at any time.`,
            confirmLabel: "Start impersonation",
        });
        if (!ok) return;
        try {
            const r = await api.post(`/users/${u.id}/impersonate`);
            const origToken = localStorage.getItem("analyzio_token");
            const origUser = localStorage.getItem("analyzio_user");
            if (origToken && origUser && !localStorage.getItem("analyzio_orig_token")) {
                localStorage.setItem("analyzio_orig_token", origToken);
                localStorage.setItem("analyzio_orig_user", origUser);
            }
            localStorage.setItem("analyzio_token", r.data.access_token);
            localStorage.setItem("analyzio_user", JSON.stringify(r.data.user));
            toast.success(`Now impersonating ${u.name || u.email}`);
            window.location.assign("/home");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impersonation failed");
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = users;
        if (q) {
            list = list.filter((u) =>
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                (u.org_name || "").toLowerCase().includes(q) ||
                (u.tenant_name || "").toLowerCase().includes(q) ||
                (ROLE_LABELS[u.role] || "").toLowerCase().includes(q),
            );
        }
        if (orgFilter !== "all") {
            list = list.filter((u) => u.org_id === orgFilter);
        }
        if (roleFilter !== "all") {
            list = list.filter((u) => u.role === roleFilter);
        }
        if (statusFilter !== "all") {
            if (statusFilter === "active")   list = list.filter((u) =>  u.is_active && !u.is_locked);
            if (statusFilter === "disabled") list = list.filter((u) => !u.is_active);
            if (statusFilter === "locked")   list = list.filter((u) =>  u.is_locked);
        }
        const getter = SORT_FIELDS[sortBy] || SORT_FIELDS.created_at;
        return [...list].sort((a, b) => {
            const va = getter(a), vb = getter(b);
            if (va < vb) return sortDir === "asc" ? -1 : 1;
            if (va > vb) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }, [users, query, orgFilter, roleFilter, statusFilter, sortBy, sortDir]);

    const activeFilterCount = (orgFilter !== "all" ? 1 : 0) + (roleFilter !== "all" ? 1 : 0) + (statusFilter !== "all" ? 1 : 0);
    const clearAllFilters = () => { setQuery(""); setOrgFilter("all"); setRoleFilter("all"); setStatusFilter("all"); };

    // Available org options derived from either the SA-only /organizations
    // payload or, as a fallback, the org_name fields on the user rows
    // themselves (so OA's filter dropdown still works).
    const orgOptions = useMemo(() => {
        if (orgs.length) return orgs.map((o) => ({ id: o.id, name: o.name }));
        const seen = new Map();
        users.forEach((u) => { if (u.org_id && !seen.has(u.org_id)) seen.set(u.org_id, u.org_name || u.org_id); });
        return Array.from(seen, ([id, name]) => ({ id, name }));
    }, [orgs, users]);

    const activeUserCount = useMemo(() => users.filter((u) => u.is_active && !u.is_locked).length, [users]);

    const toggleSort = (field) => {
        if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortBy(field); setSortDir("asc"); }
    };

    const openEdit = (u) => setEditTarget(u);

    const resendInvite = async (inv) => {
        try {
            const r = await api.post(`/auth/invite/${inv.id}/resend`);
            if (r.data.email_sent) toast.success(`Invitation re-sent to ${inv.email}`);
            else toast.warning(`Link refreshed for ${inv.email}. Email delivery failed: ${r.data.email_detail}`);
            loadInvites();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Resend failed");
        }
    };

    const deleteInvite = async (inv) => {
        const ok = await confirm({
            title: `Revoke invitation for ${inv.email}?`,
            description: "The invitation link will stop working immediately. You can always send a fresh invite later.",
            confirmLabel: "Revoke invitation",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/auth/invite/${inv.id}`);
            setInvites((prev) => prev.filter((x) => x.id !== inv.id));
            toast.success("Invitation revoked");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Revoke failed");
        }
    };

    const toggleActive = async (u) => {
        if (u.id === me?.id) { toast.error("You cannot deactivate your own account"); return; }
        const ok = await confirm({
            title: `${u.is_active ? "Deactivate" : "Activate"} ${u.name}?`,
            description: u.is_active
                ? "They will immediately lose access to the workspace. You can reactivate them at any time."
                : "They'll regain access on their next sign-in.",
            confirmLabel: u.is_active ? "Deactivate user" : "Activate user",
            destructive: !!u.is_active,
        });
        if (!ok) return;
        try {
            const r = await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
            setUsers((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
            toast.success(`${u.name} ${u.is_active ? "deactivated" : "activated"}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    // iter-120 round-4 follow-up #13 — Unlock action used by the
    // Users table when a user is currently brute-force-locked.
    // Confirms first (this is a security-sensitive action), then
    // POST /users/{id}/unlock which clears `locked_until` and
    // `failed_logins`. SA can unlock anyone; OA limited to own org.
    const unlock = async (u) => {
        const ok = await confirm({
            title: `Unlock ${u.name}'s account?`,
            description: `${u.email} is currently locked by brute-force protection. Unlocking clears the lock and resets the failed-login counter so they can sign in again immediately.`,
            confirmLabel: "Unlock account",
        });
        if (!ok) return;
        try {
            const r = await api.post(`/users/${u.id}/unlock`);
            setUsers((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
            toast.success(`${u.name}'s account unlocked`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Unlock failed");
        }
    };

    // iter-120 round-4 follow-up #14 — Manual lock action. Lets
    // admins proactively suspend an account (e.g. compromised
    // credentials) without waiting for 5 failed login attempts.
    // Confirms before locking (destructive); cannot lock self.
    const lock = async (u) => {
        if (u.id === me?.id) { toast.error("You cannot lock your own account"); return; }
        const ok = await confirm({
            title: `Lock ${u.name}'s account?`,
            description: `${u.email} will be unable to sign in until you (or another admin) unlocks the account from this page.`,
            confirmLabel: "Lock account",
            destructive: true,
        });
        if (!ok) return;
        try {
            const r = await api.post(`/users/${u.id}/lock`);
            setUsers((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
            toast.success(`${u.name}'s account locked`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Lock failed");
        }
    };

    const remove = async (u) => {
        if (u.id === me?.id) { toast.error("You cannot delete yourself"); return; }
        const ok = await confirm({
            title: `Delete ${u.name}?`,
            description: `This permanently removes ${u.email} and all of their personal data. This cannot be undone.`,
            confirmLabel: "Delete user",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/users/${u.id}`);
            setUsers((prev) => prev.filter((x) => x.id !== u.id));
            toast.success("User deleted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const filteredInvites = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return invites;
        return invites.filter((i) =>
            (i.email || "").toLowerCase().includes(q) ||
            (i.name || "").toLowerCase().includes(q) ||
            (i.tenant_name || "").toLowerCase().includes(q) ||
            (i.org_name || "").toLowerCase().includes(q) ||
            (ROLE_LABELS[i.role] || "").toLowerCase().includes(q),
        );
    }, [invites, query]);

    return (
        <div className="fadein">
            <PageHeader
                eyebrow="People"
                title="Users"
                description="Invite teammates, assign roles, and manage access."
                testId="users-header"
                actions={
                    canManage && (
                        usersLimitReached ? (
                            <Button variant="outline" className="rounded-sm opacity-90"
                                title="You've reached your plan's user limit."
                                onClick={() => openUpgrade()}
                                data-testid="invite-user-button-locked">
                                <Lock className="h-4 w-4 mr-1.5" /> Invite user
                                <Badge variant="secondary" className="ml-2 text-[9px] font-mono uppercase">Upgrade</Badge>
                            </Button>
                        ) : (
                            <Button onClick={() => setInviteOpen(true)} className="rounded-sm" data-testid="invite-user-button">
                                <UserPlus className="h-4 w-4 mr-1.5" /> Invite user
                            </Button>
                        )
                    )
                }
            />
            <PageContent>
                {/* iter-121.99 — KPI cards above filters (matches Users screenshot). */}
                <UsersStatsDeck
                    totalUsers={users.length}
                    activeUsers={activeUserCount}
                    pendingInvites={invites.length}
                />

                {/* Filter row: search + org / role / status dropdowns + clear + filters chip */}
                <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="users-filter-row">
                    <div className="relative flex-1 min-w-[260px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name, email, org, or role…"
                            className="w-full h-10 pl-9 pr-3 bg-transparent border border-border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            data-testid="users-search-input" />
                    </div>
                    <Select value={orgFilter} onValueChange={setOrgFilter}>
                        <SelectTrigger className="h-10 w-[180px] rounded-md text-sm" data-testid="users-filter-org">
                            <SelectValue placeholder="All organizations" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all" data-testid="users-filter-org-all">All organizations</SelectItem>
                            {orgOptions.map((o) => (
                                <SelectItem key={o.id} value={o.id} data-testid={`users-filter-org-${o.id}`}>{o.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="h-10 w-[150px] rounded-md text-sm" data-testid="users-filter-role">
                            <SelectValue placeholder="All roles" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all" data-testid="users-filter-role-all">All roles</SelectItem>
                            {Object.entries(ROLE_LABELS).map(([v, l]) => (
                                <SelectItem key={v} value={v} data-testid={`users-filter-role-${v}`}>{l}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-10 w-[140px] rounded-md text-sm" data-testid="users-filter-status">
                            <SelectValue placeholder="All status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all" data-testid="users-filter-status-all">All status</SelectItem>
                            <SelectItem value="active" data-testid="users-filter-status-active">Active</SelectItem>
                            <SelectItem value="disabled" data-testid="users-filter-status-disabled">Disabled</SelectItem>
                            <SelectItem value="locked" data-testid="users-filter-status-locked">Locked</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        variant="ghost" size="sm" onClick={clearAllFilters}
                        className="h-10 rounded-md text-muted-foreground hover:text-foreground"
                        data-testid="users-filter-clear">
                        Clear
                    </Button>
                    {/* <div className="inline-flex items-center gap-2 h-10 px-3 border border-border rounded-md text-sm text-muted-foreground ml-auto" data-testid="users-filter-chip">
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold bg-primary text-primary-foreground" data-testid="users-filter-count">
                                {activeFilterCount}
                            </span>
                        )}
                    </div> */}
                </div>

                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="h-auto p-0 bg-transparent border-b border-border w-full justify-start rounded-none mb-4">
                        <TabsTrigger value="active"
                            className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 -mb-px"
                            data-testid="users-tab-active">
                            Active users <span className="ml-2 text-[10px] font-mono text-muted-foreground">{users.length}</span>
                        </TabsTrigger>
                        <TabsTrigger value="pending"
                            className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 -mb-px"
                            data-testid="users-tab-pending">
                            Pending invitations <span className="ml-2 text-[10px] font-mono text-muted-foreground">{invites.length}</span>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="active" className="mt-0">
                        <ActiveUsersTable
                            filtered={filtered} me={me} canManage={canManage} canDelete={canDelete}
                            openEdit={openEdit} toggleActive={toggleActive} remove={remove}
                            canImpersonate={canImpersonate} onImpersonate={impersonate}
                            onUnlock={unlock}
                            onLock={lock}
                            sortBy={sortBy} sortDir={sortDir} toggleSort={toggleSort}
                        />
                    </TabsContent>

                    <TabsContent value="pending" className="mt-0">
                        <PendingInvitesTable
                            rows={filteredInvites} canManage={canManage}
                            onResend={resendInvite} onDelete={deleteInvite}
                        />
                    </TabsContent>
                </Tabs>
            </PageContent>

            <InviteUserDialog
                open={inviteOpen} onOpenChange={setInviteOpen}
                me={me} users={users} orgs={orgs} tenantsByOrg={tenantsByOrg}
                loadTenantsForOrg={loadTenantsForOrg}
                onSuccess={() => { load(); loadInvites(); }}
            />
            <EditUserDialog
                target={editTarget} onOpenChange={(o) => !o && setEditTarget(null)}
                me={me} orgs={orgs} tenantsByOrg={tenantsByOrg}
                loadTenantsForOrg={loadTenantsForOrg}
                onSaved={(updated) => {
                    setUsers((prev) => prev.map((x) => x.id === updated.id ? updated : x));
                    setEditTarget(null);
                }}
            />
        </div>
    );
}
